/**
 * UI-31: Action Card — Decision-Force Interface
 *
 * First card = THE decision. Left accent bar, dominant scale, immediate CTA.
 * Secondary cards = supporting context, visually receded.
 *
 * P1: Force first decision — left accent bar, larger text, stronger CTA
 * P2: De-emphasize 2–5 — compact, muted, no strong borders
 * P4: Signal-based context — "Based on N active signals" instead of fake labels
 * P7: Minimalism — hierarchy through spacing + contrast, not badges
 *
 * PILOT-23: targetItems renders specific rooms before pressing.
 *           Double-render of contextLine fixed (operationalContext hidden when
 *           contextLine already shows the same text).
 */

import type { Action, ActionPriority, ActionDomain, ActionCTA } from '../../services/action-engine/actionTypes';
import { completionVerb } from '../../hooks/useActionExecution';
import type { ActionResult } from '../../hooks/useActionExecution';

// ─── Priority Styles ─────────────────────────────────────────────────────────

const PRIORITY_CONFIG: Record<ActionPriority, {
  accent: string;        // left accent bar color (first card)
  dot: string;           // priority dot (secondary cards)
  bgFirst: string;       // background for dominant card
  bgSecondary: string;   // background for secondary cards
}> = {
  critical: {
    accent: 'border-l-rose-500',
    dot: 'bg-rose-400',
    bgFirst: 'bg-rose-500/10 border border-rose-500/30',
    bgSecondary: 'bg-slate-900/40',
  },
  high: {
    accent: 'border-l-amber-500',
    dot: 'bg-amber-400',
    bgFirst: 'bg-amber-500/8 border border-amber-500/25',
    bgSecondary: 'bg-slate-900/40',
  },
  medium: {
    accent: 'border-l-slate-500',
    dot: 'bg-slate-400',
    bgFirst: 'bg-slate-800/70 border border-slate-600/50',
    bgSecondary: 'bg-slate-900/40',
  },
};

const DOMAIN_LABEL: Record<ActionDomain, string> = {
  housekeeping: 'HOUSEKEEPING',
  operations: 'OPERATIONS',
  maintenance: 'MAINTENANCE',
  staff: 'STAFF',
};

/**
 * Fallback operational context text shown only when contextLine is absent.
 * PILOT-23 fix: previously both contextLine and operationalContext() were shown
 * at the same time when contextLine was set (double-render bug).
 */
function fallbackContextLine(action: Action): string {
  const n = action.signalCount;
  switch (action.domain) {
    case 'housekeeping': return `${n} room${n !== 1 ? 's' : ''} waiting for cleaning`;
    case 'operations': return `${n} request${n !== 1 ? 's' : ''} requiring attention`;
    case 'maintenance': return `${n} issue${n !== 1 ? 's' : ''} pending resolution`;
    case 'staff': return `${n} task${n !== 1 ? 's' : ''} pending across staff`;
    default: return `${n} item${n !== 1 ? 's' : ''} requiring action`;
  }
}

function ctaLabel(cta: ActionCTA): string {
  if (cta.type === 'navigate') return 'Go to ' + (cta.to.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') ?? 'page');
  if (cta.type === 'dispatch') return 'Dispatch Now';
  if (cta.type === 'assign') return 'Reassign Now';
  if (cta.type === 'escalate') return 'Escalate Now';
  // OPS-38.2: explicit verbs for prioritize / recover — see actionTypes.ts.
  if (cta.type === 'prioritize') return 'Prioritize Now';
  // PILOT-23: "Handle Now" replaces "Run Recovery" — supervisor language.
  if (cta.type === 'recover') return 'Handle Now';
  return 'Take Action';
}

// ─── PILOT-23: Target Items Pill Row ─────────────────────────────────────────

const MAX_TARGET_DISPLAY = 3;

function TargetItems({ action }: { action: Action }) {
  const items = action.targetItems;
  if (!items || items.length === 0) return null;

  const display = items.slice(0, MAX_TARGET_DISPLAY);
  const remainder = items.length - display.length;

  return (
    <div data-testid="action-target-items" className="flex flex-wrap gap-1 mt-1.5">
      {display.map(item => (
        <span
          key={item.id}
          className="inline-flex items-center gap-1 text-[9px] font-medium rounded bg-slate-700/60 px-1.5 py-0.5 text-slate-300"
        >
          <span className="font-semibold">Rm {item.roomNumber}</span>
          <span className="text-slate-500">·</span>
          {/* Maintenance: show truncated issue description instead of generic label */}
          <span>{item.domain === 'maintenance' && item.description ? item.description : item.categoryLabel}</span>
          <span className="text-slate-500">·</span>
          <span className="text-amber-400/80">{item.ageMinutes}m</span>
        </span>
      ))}
      {remainder > 0 && (
        <span className="inline-flex items-center text-[9px] text-slate-500 px-1 py-0.5">
          +{remainder} more
        </span>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ActionCardProps {
  action: Action;
  isFirst?: boolean;
  executing?: boolean;
  completed?: boolean;
  actionResult?: ActionResult;
  onExecute: (action: Action) => void;
}

/** Completion label derives from ActionResult or CTA verb (P2) */
function completedLabel(action: Action, result?: ActionResult): string {
  if (result?.message) return result.message;
  return completionVerb(action.cta);
}

export function ActionCard({ action, isFirst = false, executing = false, completed = false, actionResult, onExecute }: ActionCardProps) {
  const p = PRIORITY_CONFIG[action.priority];

  // ─── Dominant first card (P1) ───────────────────────────────────────────────
  if (isFirst) {
    return (
      <div
        data-testid="action-card"
        data-first={true}
        className={`rounded-xl border-l-[3px] ${p.accent} ${p.bgFirst} px-4 py-3.5 shadow-md`}
      >
        <p data-testid="top-priority-label" className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
          Top priority
        </p>

        <div className="flex items-start gap-3">
          <span className={`mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${p.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-100 text-[13px] leading-tight">
                {action.title}
              </h3>
              <span className="flex-shrink-0 text-[8px] font-bold uppercase tracking-wider text-slate-500 px-1 py-0.5 rounded bg-slate-700/50">
                {DOMAIN_LABEL[action.domain]}
              </span>
            </div>
            {/* PILOT-23: contextLine shows specific rooms when available;
                fallback text shown only when contextLine is absent (fixes double-render) */}
            <p data-testid="action-context" className="text-[11px] text-slate-400 leading-snug line-clamp-1 mt-0.5">
              {action.contextLine ?? fallbackContextLine(action)}
            </p>
            {/* PILOT-23: specific room pills — shown below the context line */}
            <TargetItems action={action} />
          </div>
          <button
            type="button"
            data-testid="action-cta"
            disabled={executing || completed}
            aria-disabled={executing || completed}
            onClick={() => onExecute(action)}
            className={`flex-shrink-0 text-xs font-medium rounded-lg text-white px-3.5 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${
              completed
                ? actionResult?.status === 'warning'
                  ? 'bg-amber-800/50 text-amber-300 cursor-default'
                  : 'bg-emerald-700/50 cursor-default'
                : executing
                  ? 'bg-emerald-600/50 cursor-wait'
                  : 'bg-emerald-600 hover:bg-emerald-500'
            }`}
          >
            {completed ? completedLabel(action, actionResult) : executing ? 'Executing…' : ctaLabel(action.cta)}
          </button>
        </div>
      </div>
    );
  }

  // ─── Secondary cards (P2) — compact, muted ─────────────────────────────────
  // P4: completed cards fade to 70% (not 50%) with transition delay
  return (
    <div
      data-testid="action-card"
      className={`rounded-lg ${p.bgSecondary} px-3 py-2 transition-all duration-700 ${completed ? 'opacity-70' : 'hover:bg-slate-800/60'}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-slate-300 text-xs leading-tight truncate">
              {action.title}
            </h3>
            <span className="flex-shrink-0 text-[8px] font-bold uppercase tracking-wider text-slate-600 px-1 py-0.5 rounded bg-slate-800/60">
              {DOMAIN_LABEL[action.domain]}
            </span>
          </div>
          {/* PILOT-23: contextLine or fallback (no double-render) */}
          <p data-testid="action-context" className="text-[10px] text-slate-500 leading-snug line-clamp-1 mt-0.5">
            {action.contextLine ?? fallbackContextLine(action)}
          </p>
          {/* PILOT-23: target pills on secondary cards */}
          <TargetItems action={action} />
        </div>
        <button
          type="button"
          data-testid="action-cta"
          disabled={executing || completed}
          aria-disabled={executing || completed}
          onClick={() => onExecute(action)}
          className={`flex-shrink-0 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 rounded px-2 py-1 ${
            completed
              ? actionResult?.status === 'warning'
                ? 'text-amber-500 cursor-default'
                : 'text-emerald-600 cursor-default'
              : executing
                ? 'text-emerald-500/50 cursor-wait'
                : 'text-emerald-400 hover:text-emerald-300'
          }`}
        >
          {completed ? completedLabel(action, actionResult) : executing ? 'Executing…' : ctaLabel(action.cta)}
        </button>
      </div>
    </div>
  );
}
